import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { Button } from "./button";

/** "Showing 20 of 214 · Load more" footer for long lists. */
export function LoadMore({
  shown,
  total,
  noun,
  onMore,
  step,
}: {
  shown: number;
  total: number;
  noun: string;
  onMore: () => void;
  step?: number;
}) {
  if (total === 0) return null;
  return (
    <div className="flex items-center gap-3 border-t border-border-subtle px-4 py-2 text-sm text-fg-subtle">
      <span>
        Showing {Math.min(shown, total)} of {total} {noun}
      </span>
      {shown < total ? (
        <Button variant="link" size="sm" onClick={onMore}>
          Load {step ? `${Math.min(step, total - shown)} ` : ""}more
        </Button>
      ) : null}
    </div>
  );
}

export interface DescriptionItem {
  label: ReactNode;
  value: ReactNode;
  mono?: boolean;
}

/** Two-column label/value list for detail panels. */
export function DescriptionList({
  items,
  className,
  columns = 1,
}: {
  items: DescriptionItem[];
  className?: string;
  columns?: 1 | 2 | 3;
}) {
  return (
    <dl
      className={cn(
        "grid gap-x-6 gap-y-2",
        columns === 2 && "sm:grid-cols-2",
        columns === 3 && "sm:grid-cols-3",
        className,
      )}
    >
      {items.map((it, i) => (
        <div key={typeof it.label === "string" ? it.label : i} className="flex flex-col gap-0.5">
          <dt className="text-xs font-medium tracking-wider text-fg-subtle uppercase">
            {it.label}
          </dt>
          <dd className={cn("text-base text-fg", it.mono && "font-mono text-sm")}>{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Section heading inside a page or drawer body. */
export function SectionTitle({
  children,
  meta,
  actions,
  className,
}: {
  children: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <h3 className="font-semibold text-fg">{children}</h3>
      {meta ? <span className="text-sm text-fg-subtle">{meta}</span> : null}
      <span className="flex-1" />
      {actions}
    </div>
  );
}

/** Row of filter controls above a table. */
export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 border-b border-border-subtle px-3 py-2",
        className,
      )}
    >
      {children}
    </div>
  );
}
